import axios, { AxiosError } from 'axios';
import { envConfig } from '@/config/env';
import { getSession } from 'next-auth/react';

interface RefreshTokenResponse {
  accessToken: string;
  expiresIn: number;
  refreshToken?: string;
}

let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function subscribeTokenRefresh(callback: (token: string) => void): void {
  refreshSubscribers.push(callback);
}

function onRefreshComplete(token: string): void {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

export async function manualRefreshToken(): Promise<string | null> {
  try {
    const session = await getSession();

    if (!session?.refreshToken) {
      console.error('No refresh token available');
      return null;
    }

    if (isRefreshing) {
      return new Promise((resolve) => {
        subscribeTokenRefresh((token: string) => {
          resolve(token || null);
        });
      });
    }

    isRefreshing = true;

    const response = await axios.post<RefreshTokenResponse>(
      `${envConfig.apiUrl}/auth/refresh`,
      {
        refreshToken: session.refreshToken,
      }
    );

    const newAccessToken = response.data.accessToken;

    onRefreshComplete(newAccessToken);
    isRefreshing = false;

    return newAccessToken;
  } catch (error: unknown) {
    console.error('Token refresh failed:', error);
    isRefreshing = false;
    onRefreshComplete('');

    if (error instanceof AxiosError && error.response?.status === 401) {
      console.error(
        'Refresh token is invalid or user is inactive. User will be logged out.'
      );
    }

    return null;
  }
}

export async function getTimeUntilExpiration(): Promise<number> {
  const session = await getSession();

  if (!session?.accessTokenExpires) {
    return 0;
  }

  const timeUntilExpiry = session.accessTokenExpires - Date.now();
  return Math.max(0, timeUntilExpiry);
}

export async function needsRefresh(): Promise<boolean> {
  const timeUntilExpiry = await getTimeUntilExpiration();
  const oneMinute = 60 * 1000;
  return timeUntilExpiry < oneMinute && timeUntilExpiry > 0;
}

export async function isTokenExpired(): Promise<boolean> {
  const timeUntilExpiry = await getTimeUntilExpiration();
  return timeUntilExpiry <= 0;
}

export const refreshTokenService = {
  manualRefreshToken,
  getTimeUntilExpiration,
  needsRefresh,
  isTokenExpired,
};
